/**
 * Status pill (Phase 65 / BLU-594 PR2). Emerald = ready (derivable), amber =
 * attention. EditingPill geometry (ring, not border) per DESIGN §7/§13.1; two
 * tones, always text (DESIGN §16). Copy is resolved from the payload-derived
 * descriptor so an `unmapped_values` product reads "1 colour unmapped", not a
 * generic label.
 *
 * Phase 92 (92-27, UI-SPEC 1.5): a product being built on a 3PL's blank can
 * carry a `supply` state instead, all amber: "Choose a blank", "Match {count}
 * values" or "Print SKU in use". The row decides which applies (92-27b); with
 * no `supply` prop the pill is exactly today's.
 */
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { describeStatus } from '../lib/status';
import type { PreviewProduct } from '../lib/types';

/** A supply product that still needs the client (UI-SPEC 1.5). */
export type SupplyPillState =
  | { kind: 'choose_blank' }
  | { kind: 'match_values'; count: number }
  | { kind: 'print_sku_in_use' }
  /** A saved choice that can be built (UI-SPEC 1.5 reuses "Ready", 92-27b). */
  | { kind: 'ready' };

interface StatusPillProps {
  product: PreviewProduct;
  /** Overrides the derivation status for a product on a 3PL's blank (92-27b wires it). */
  supply?: SupplyPillState | null;
}

const SUPPLY_LABEL_KEY: Record<SupplyPillState['kind'], string> = {
  choose_blank: 'supply.pill.chooseBlank',
  match_values: 'supply.pill.matchValues',
  print_sku_in_use: 'supply.pill.printSkuInUse',
  ready: 'status.ready',
};

export function StatusPill({ product, supply = null }: StatusPillProps) {
  const { t } = useTranslation('productSync');
  const desc = supply
    ? {
        tone: supply.kind === 'ready' ? ('ready' as const) : ('attention' as const),
        key: SUPPLY_LABEL_KEY[supply.kind],
        values: supply.kind === 'match_values' ? { count: supply.count } : undefined,
      }
    : describeStatus(product);
  const label = t(desc.key, desc.values);

  return (
    <span
      className={cn(
        'inline-flex w-fit items-center gap-1 whitespace-nowrap rounded-md px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1',
        desc.tone === 'ready'
          ? 'bg-emerald-100 text-emerald-800 ring-emerald-300/80'
          : 'bg-amber-100 text-amber-800 ring-amber-300/80',
      )}
    >
      {label}
    </span>
  );
}
